import { Button, Container, Grid, Group, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import UpdatePOSPage from '../../pages/auth-pages/update-pos/update-pos.page';
import { IPaginationMeta } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';
import { BasicPOSs, RetrievePOSsResponse } from '../../type/pos.types';
import SearchButton from '../buttons/search-button/search-button.component';
import SelectBranchFilter from '../filters/select-branch-filter.component';
import SelectMerchantFilter from '../filters/select-merchant-filter.component';
import SelectPartnerFilter from '../filters/select-partner-filter.component';
import SelectResellerFilter from '../filters/select-reseller-filter.component';
import XPagination from '../x-components/x-pagination.component';
import XPaper from '../x-components/x-paper.component';
import POSsTable from './components/pos-table.component';
import ViewPosInfo from './view-pos-info.component';

function ViewAllPOS() {
  const [search, setSearch] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [componentManager, setComponentManager] = useState(OpenComponent.HOME);
  const [selectedPOSId, setSelectedPOSId] = useState<number | undefined>();
  const [params, setParams] = useState<{
    page: number;
    limit: number;
    partnerId?: string;
    resellerId?: string;
    merchantId?: string;
    branchId?: string;
    posName?: string;
  }>({
    limit: 20,
    page: 1,
  });
  const [pos, setPos] = useState<BasicPOSs[]>();
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      partner: '',
      reseller: '',
      merchant: '',
      branch: '',
    },
  });
  const { auth } = useAuth();
  const isViewOnly =
    auth?.role === Roles.SETTLEMENT || auth?.role === Roles.CHECKER;
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);

  const getDynamicFilter = () => {
    let filters: JSX.Element = <></>;

    if (
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <Grid.Col xs={12} sm={6}>
          <SelectPartnerFilter
            filterSelectData={filterSelectData}
            filterValues={filterValues}
          />
        </Grid.Col>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectResellerFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isPartnerDependent={
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER
              }
            />
          </Grid.Col>
        </>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.RESELLER ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectMerchantFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isResellerDependent={
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER
              }
            />
          </Grid.Col>
        </>
      );
    }

    if (
      auth?.role === Roles.PARTNER ||
      auth?.role === Roles.RESELLER ||
      auth?.role === Roles.MERCHANT ||
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      filters = (
        <>
          {filters}
          <Grid.Col xs={12} sm={6}>
            <SelectBranchFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isMerchantDependent={
                auth?.role === Roles.RESELLER ||
                auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER
              }
            />
          </Grid.Col>
        </>
      );
    }

    return <Grid>{filters}</Grid>;
  };

  const loadPOSs = async () => {
    try {
      setIsLoading(true);

      const response = await axiosPrivate.get('/web/pos', {
        params: removeEmptyPropertiesWithNested(params),
      });

      const data: RetrievePOSsResponse = response.data;

      setPaginationData(data?.metadata);
      setPos(data.items);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-pos',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const paginationRequest = (page: number, limit: number) => {
    setParams((prev) => {
      return {
        ...prev,
        page,
        limit,
      };
    });
  };

  useEffect(() => {
    loadPOSs();
    setIsChecked([]);
    setCheckedAll(false);
  }, [params]);

  useDidMountEffect(() => {
    setParams((prev) => {
      return {
        ...prev,
        partnerId: filterValues.values.partner,
        resellerId: filterValues.values.reseller,
        merchantId: filterValues.values.merchant,
        branchId: filterValues.values.branch,
        page: 1,
      };
    });
  }, [filterValues.values]);

  useDidMountEffect(() => {
    setParams((prev) => {
      return {
        ...prev,
        posName: search,
        page: 1,
      };
    });
  }, [search]);

  return (
    <>
      {componentManager === OpenComponent.HOME ? (
        <>
          <Title order={2} my='lg'>
            View All POS
          </Title>

          <XPaper>
            <Container p='lg' fluid>
              {getDynamicFilter()}
            </Container>
            {!isViewOnly && (
              <Group p='md' position='right'>
                <Button
                  onClick={() => setComponentManager(OpenComponent.UPDATE_USER)}
                  disabled={isChecked.length !== 1}
                >
                  UPDATE
                </Button>
              </Group>
            )}

            <Group p='md' position='right'>
              <SearchButton
                setSearch={setSearch}
                disabled={isLoading}
                search={search}
              />
            </Group>

            <POSsTable
              pos={pos}
              isLoading={isLoading}
              setComponentManager={setComponentManager}
              setSelectedPOSId={setSelectedPOSId}
              checkedAll={checkedAll}
              setCheckedAll={setCheckedAll}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              isViewOnly={isViewOnly}
            />

            {paginationData && paginationData.totalPages ? (
              <XPagination
                total={paginationData.totalPages}
                page={paginationData.currentPage}
                limit={params.limit}
                paginationRequest={paginationRequest}
              />
            ) : (
              <></>
            )}
          </XPaper>
        </>
      ) : componentManager === OpenComponent.VIEW_USER && selectedPOSId ? (
        <ViewPosInfo
          setComponentManager={setComponentManager}
          selectedPOSId={selectedPOSId}
          setSelectedPOS={setSelectedPOSId}
        />
      ) : componentManager === OpenComponent.UPDATE_USER && !isViewOnly ? (
        <UpdatePOSPage
          setComponentManager={setComponentManager}
          posId={isChecked[0]}
          loadPOSs={loadPOSs}
        />
      ) : (
        <>Cannot access</>
      )}
    </>
  );
}
export default ViewAllPOS;
